import React, { useRef, useState, useCallback } from 'react'
import { NavButton, NavButtonDirection } from '@wallyhealth/web-core'
import NavigationStep from './NavigationStep'
import styles from './index.module.css'

const ArrivalGuide = ({ steps }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [hasNavigated, setHasNavigated] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const touchStartRef = useRef({ x: 0, y: 0, locked: false })
  const mouseStartRef = useRef({ x: 0, y: 0, isDown: false })
  const animationTimerRef = useRef(null)

  const goTo = useCallback((nextIndex) => {
    setHasNavigated(true)
    setIsAnimating(true)
    setCurrentStepIndex(nextIndex)
    setDragOffset(0)

    if (animationTimerRef.current) clearTimeout(animationTimerRef.current)
    animationTimerRef.current = setTimeout(() => setIsAnimating(false), 300)
  }, [])

  const handleNext = useCallback(() => {
    if (!steps || currentStepIndex >= steps.length - 1) return
    goTo(currentStepIndex + 1)
  }, [steps, currentStepIndex, goTo])

  const handleBack = useCallback(() => {
    if (currentStepIndex <= 0) return
    goTo(currentStepIndex - 1)
  }, [currentStepIndex, goTo])

  if (!steps || steps.length === 0) return null

  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === steps.length - 1
  const showSwipeHint = isFirstStep && !isLastStep && !hasNavigated
  const SWIPE_THRESHOLD = 50

  const clampDrag = (delta) => {
    if (delta > 0 && isFirstStep) return delta * 0.2
    if (delta < 0 && isLastStep) return delta * 0.2
    return delta
  }

  const commitDrag = (delta) => {
    if (delta < -SWIPE_THRESHOLD && !isLastStep) {
      goTo(currentStepIndex + 1)
    } else if (delta > SWIPE_THRESHOLD && !isFirstStep) {
      goTo(currentStepIndex - 1)
    } else {
      setDragOffset(0)
    }
  }

  const cancelDrag = () => {
    setIsDragging(false)
    setDragOffset(0)
  }

  const handleTouchStart = (event) => {
    if (isAnimating) return
    const touch = event.touches[0]
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, locked: false }
    setIsDragging(true)
  }

  const handleTouchMove = (event) => {
    if (!isDragging || isAnimating) return
    const touch = event.touches[0]
    const deltaX = touch.clientX - touchStartRef.current.x
    const deltaY = touch.clientY - touchStartRef.current.y

    if (!touchStartRef.current.locked) {
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        setIsDragging(false)
        return
      }
      touchStartRef.current.locked = true
    }

    event.preventDefault()
    setDragOffset(clampDrag(deltaX))
  }

  const handleTouchEnd = () => {
    if (!isDragging) return
    setIsDragging(false)
    commitDrag(dragOffset)
  }

  const handleMouseDown = (event) => {
    if (event.button !== 0 || isAnimating) return
    mouseStartRef.current = { x: event.clientX, y: event.clientY, isDown: true }
    setIsDragging(true)
  }

  const handleMouseMove = (event) => {
    if (!mouseStartRef.current.isDown || isAnimating) return
    const deltaX = event.clientX - mouseStartRef.current.x
    setDragOffset(clampDrag(deltaX))
  }

  const handleMouseUp = () => {
    if (!mouseStartRef.current.isDown) return
    mouseStartRef.current.isDown = false
    setIsDragging(false)
    commitDrag(dragOffset)
  }

  const handleMouseLeave = () => {
    if (mouseStartRef.current.isDown) {
      mouseStartRef.current.isDown = false
      setIsDragging(false)
      commitDrag(dragOffset)
    }
  }

  const trackStyle = {
    transform: `translateX(calc(-${currentStepIndex} * (100% + 1.5rem) + ${dragOffset}px))`,
    transition: isDragging ? 'none' : 'transform 0.3s ease'
  }

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.toolbar_left}>
          <span className={styles.toolbar_label}>When you arrive</span>
          <span className={styles.toolbar_count}>{currentStepIndex + 1}/{steps.length}</span>
        </div>
        <div className={styles.toolbar_right}>
          <NavButton
            direction={NavButtonDirection.PREV}
            onClick={handleBack}
            isDisabled={isFirstStep}
            ariaLabel="Previous step"
          />
          <NavButton
            direction={NavButtonDirection.NEXT}
            onClick={handleNext}
            isDisabled={isLastStep}
            ariaLabel="Next step"
          />
        </div>
      </div>

      <div className={styles.progress_track}>
        <span
          className={styles.progress_fill}
          style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
        />
      </div>

      <div
        className={styles.content}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={cancelDrag}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onDragStart={(event) => event.preventDefault()}
      >
        <div
          className={styles.track}
          style={trackStyle}
        >
          {steps.map((step, index) => (
            <NavigationStep
              key={index}
              step={step}
              showSwipeHint={showSwipeHint && index === 0}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default ArrivalGuide
